import { create } from 'zustand';
import type { CreateUserDto, NetSuiteEmployeeDto, UserDto } from '../types';
import { userService } from '../services';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';

interface UserState {
  // Users list state
  users: UserDto[];
  isUsersLoading: boolean;
  usersError: string | null;

  // Create user state
  isSubmittingUser: boolean;
  userSubmitError: string | null;

  // NetSuite employee search state
  employeeSearchResults: NetSuiteEmployeeDto[];
  isSearchingEmployees: boolean;
  employeeSearchError: string | null;

  // Actions
  fetchUsers: () => Promise<void>;
  clearError: () => void;
  createUser: (userData: CreateUserDto) => Promise<boolean>;

  // NetSuite employee search actions
  searchEmployees: (search: string) => Promise<void>;
  clearEmployeeSearch: () => void;
}

export const useUserStore = create<UserState>()((set, get) => ({
  // Users list state
  users: [],
  isUsersLoading: false,
  usersError: null,

  // Create user state
  isSubmittingUser: false,
  userSubmitError: null,

  // NetSuite employee search state
  employeeSearchResults: [],
  isSearchingEmployees: false,
  employeeSearchError: null,

  fetchUsers: async () => {
    set({ isUsersLoading: true, usersError: null });
    try {
      const users = await userService.getAllUsers();
      set({ users, isUsersLoading: false });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch users');
      set({ usersError: message, isUsersLoading: false });
    }
  },

  clearError: () => set({ usersError: null }),

  createUser: async (userData) => {
    set({ isSubmittingUser: true, userSubmitError: null });

    try {
      await userService.createUser(userData);
      set({ isSubmittingUser: false });
      get().fetchUsers();
      return true;
    } catch (error) {
      set({
        userSubmitError: getApiErrorMessage(error, 'Failed to create user'),
        isSubmittingUser: false,
      });
      return false;
    }
  },

  searchEmployees: async (search: string) => {
    if (search.length < 2) {
      set({ employeeSearchResults: [], isSearchingEmployees: false });
      return;
    }
    set({ isSearchingEmployees: true, employeeSearchError: null });
    try {
      const results = await userService.searchNetSuiteEmployees(search);
      set({ employeeSearchResults: results, isSearchingEmployees: false });
    } catch (error) {
      set({
        employeeSearchError: getApiErrorMessage(error, 'Failed to search employees'),
        isSearchingEmployees: false,
        employeeSearchResults: [],
      });
    }
  },

  clearEmployeeSearch: () =>
    set({ employeeSearchResults: [], employeeSearchError: null, isSearchingEmployees: false }),
}));
